import { usePage } from "@inertiajs/react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { holdsPicSlot } from "@/components/admin-portal/appointment/form/pic-slot-selectors";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import type { AppointmentBookingSchema } from "@/lib/appointments/form";
import { cn } from "@/lib/utils";
import type { AppointmentNewGlobalPageProps } from "@/pages/AdminPortal/Appointment/New";

type AdminOption = {
	id: string | number;
	name: string;
	email: string;
};

interface AdminPicMultiSelectorProps {
	disabled?: boolean;
	showDescription?: boolean;
	className?: string;
}

export const AdminPicMultiSelector = memo(function AdminPicMultiSelector({
	disabled = false,
	showDescription = true,
	className,
}: AdminPicMultiSelectorProps) {
	const { t } = useTranslation("appointments-form");
	const { props: globalProps } = usePage<AppointmentNewGlobalPageProps>();
	const form = useFormContext<AppointmentBookingSchema>();
	const [open, setOpen] = useState(false);

	const watchAdmins = useWatch({
		control: form.control,
		name: "additionalSettings.admins",
	});

	// The three slots, watched so the list below reacts the moment one is reassigned.
	const slotIds = useWatch({
		control: form.control,
		name: [
			"additionalSettings.picL1",
			"additionalSettings.picL2",
			"additionalSettings.picL3",
		],
	});
	const holdsSlot = useCallback(
		(adminId: string | number) =>
			!!globalProps.draftPicKpiEnabled && holdsPicSlot(slotIds, adminId),
		[globalProps.draftPicKpiEnabled, slotIds],
	);

	const adminOptions = useMemo(() => {
		// Transform backend admin data to match AdminOption interface
		return (globalProps.admins || [])
			.map((admin: any) => ({
				id: admin.id,
				name: admin.name,
				email: admin?.email || admin?.user?.email || "",
			}))
			.filter((admin) => !holdsSlot(admin.id));
	}, [globalProps.admins, holdsSlot]);

	// What the form actually carries — stored as the union of the slots and the supporting
	// PICs, to match appointment_draft_admins.
	const allAdmins = useMemo(() => watchAdmins || [], [watchAdmins]);
	// What this control shows: the supporting half only, which is what its label promises.
	// Slot holders are reassigned through the three selectors above, so offering them a
	// chip here would only be a remove button that removes nothing — the server merges the
	// slots back in on every save.
	const selectedAdmins = useMemo(
		() => allAdmins.filter((admin) => !holdsSlot(admin.id)),
		[allAdmins, holdsSlot],
	);

	const onSelectAdmin = useCallback(
		(admin: AdminOption) => {
			// Mutating the full value, not the filtered view: dropping the slot holders
			// here would rewrite them out of the blob on the next save.
			const currentAdmins = allAdmins;

			// Check if admin is already selected
			if (currentAdmins.find((a) => a.id === admin.id)) {
				return; // Already selected
			}

			// Add new admin
			const adminPic = {
				id: admin.id,
				name: admin.name,
				email: admin.email || "",
			};

			const updatedAdmins = [...currentAdmins, adminPic];
			form.setValue("additionalSettings.admins", updatedAdmins, {
				shouldValidate: true,
			});

			// If this is the first admin, also set it as the primary adminPic
			if (updatedAdmins.length === 1) {
				form.setValue("additionalSettings.admins", updatedAdmins, {
					shouldValidate: true,
				});
			}
		},
		[form, allAdmins],
	);

	const onRemoveAdmin = useCallback(
		(adminId: string) => {
			// Mutating the full value, not the filtered view: dropping the slot holders
			// here would rewrite them out of the blob on the next save.
			const currentAdmins = allAdmins;
			const updatedAdmins = currentAdmins.filter((a) => a.id !== adminId);

			form.setValue("additionalSettings.admins", updatedAdmins, {
				shouldValidate: true,
			});

			// If we removed the primary adminPic, update it to the first remaining admin
			const currentPrimary = form.getValues("additionalSettings.admins")?.[0];
			if (currentPrimary?.id === adminId && updatedAdmins.length > 0) {
				form.setValue("additionalSettings.admins", updatedAdmins, {
					shouldValidate: true,
				});
			} else if (updatedAdmins.length === 0) {
				form.setValue("additionalSettings.admins", [], {
					shouldValidate: true,
				});
			}
		},
		[form, allAdmins],
	);

	// Whoever the server will actually mark primary. With DRAFT_PIC_KPI on that is L1 —
	// AppointmentDraft#sync_pics! sets is_primary and admin_pic_id from it and ignores the
	// order of this list — so reading position 0 would badge somebody the save then
	// demotes. Falls back to the old rule while the flag is off, where the first admin
	// really is the primary one.
	const picL1 = useWatch({
		control: form.control,
		name: "additionalSettings.picL1",
	});
	const primaryAdminId = globalProps.draftPicKpiEnabled
		? picL1
		: form.watch("additionalSettings.admins")?.[0]?.id;
	// String-compared because the two sides disagree on type: a slot holds an id as text,
	// while the multi-select's own rows can carry it as a number.
	const isPrimaryAdmin = (adminId: string | number) =>
		!!primaryAdminId && String(primaryAdminId) === String(adminId);

	return (
		<FormField
			control={form.control}
			name="additionalSettings.admins"
			render={() => (
				<FormItem className={cn("flex flex-col", className)}>
					{/* Renamed while DRAFT_PIC_KPI is on: this list is no longer "the PICs",
					    it is everyone beyond the three slots above. The two are merged on
					    save — see AppointmentDraft#sync_pics! — so a name in either one can
					    open the Draft, and the label has to say which half this is. */}
					<FormLabel>
						{globalProps.draftPicKpiEnabled
							? t("draft.admin_pic.supporting_label", {
									defaultValue: "PIC pendukung",
								})
							: t("draft.admin_pic.label", { defaultValue: "Admin PICs" })}
					</FormLabel>

					<Popover open={open} onOpenChange={setOpen}>
						<PopoverTrigger asChild>
							<FormControl>
								<Button
									variant="outline"
									aria-expanded={open}
									disabled={disabled}
									className={cn(
										"w-full justify-between font-normal bg-sidebar shadow-inner h-auto min-h-10 py-2",
										selectedAdmins.length === 0 && "text-muted-foreground",
									)}
								>
									<div className="flex flex-wrap items-center gap-1">
										{selectedAdmins.length > 0 ? (
											selectedAdmins.map((admin) => (
												<Badge
													key={admin.id}
													variant="accent"
													className="text-xs group"
												>
													{admin.name}
													{isPrimaryAdmin(admin.id) && (
														<span className="ml-1">(Primary)</span>
													)}
													{!isPrimaryAdmin(admin.id) ? (
														// biome-ignore lint/a11y/useSemanticElements: act like a button
														<div
															onClick={(e) => {
																e.stopPropagation();
																e.preventDefault();
																onRemoveAdmin(String(admin.id));
															}}
															onKeyDown={() => {}}
															className="ml-1 hover:text-destructive opacity-70 hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1 rounded p-0.5 cursor-pointer"
															role="button"
															tabIndex={0}
															aria-label={`Remove ${admin.name}`}
														>
															<X className="h-3 w-3" />
														</div>
													) : null}
												</Badge>
											))
										) : (
											<span>
												{t("draft.admin_pic.placeholder", {
													defaultValue: "Select Admin PICs...",
												})}
											</span>
										)}
									</div>
									<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
								</Button>
							</FormControl>
						</PopoverTrigger>
						<PopoverContent
							className="p-0 w-[var(--radix-popover-trigger-width)]"
							align="start"
						>
							<Command>
								<CommandInput
									placeholder={t("draft.admin_pic.search", {
										defaultValue: "Search admin...",
									})}
								/>
								<CommandList>
									<CommandEmpty>
										{t("draft.admin_pic.empty", {
											defaultValue: "No admin found.",
										})}
									</CommandEmpty>
									<CommandGroup>
										{(() => {
											// Separate selected and unselected admins
											const selectedAdminsList = [];
											const unselectedAdminsList = [];

											for (const admin of adminOptions) {
												const isSelected = selectedAdmins.find(
													(a) => a.id === admin.id,
												);
												if (isSelected) {
													selectedAdminsList.push(admin);
												} else {
													unselectedAdminsList.push(admin);
												}
											}

											// Combine: selected admins first, then unselected
											const sortedAdmins = [
												...selectedAdminsList,
												...unselectedAdminsList,
											];

											return sortedAdmins.map((admin) => {
												const isSelected = selectedAdmins.find(
													(a) => a.id === admin.id,
												);
												const isPrimary = isPrimaryAdmin(admin.id);

												return (
													<CommandItem
														key={admin.id}
														value={admin.name}
														onSelect={() => onSelectAdmin(admin)}
														className={cn(isSelected && "bg-primary/5")}
													>
														<div className="flex items-center gap-2 flex-1">
															<Avatar className="size-6 !rounded-[0px] text-primary border border-primary">
																<AvatarFallback className="text-xs !rounded-[0px]">
																	{admin.name?.charAt(0)?.toUpperCase() || "A"}
																</AvatarFallback>
															</Avatar>
															<div className="flex flex-col">
																<span className="text-sm">{admin.name}</span>
																{admin.email && (
																	<span className="text-xs text-muted-foreground">
																		{admin.email}
																	</span>
																)}
															</div>
														</div>
														<div className="flex items-center gap-4">
															{isPrimary && (
																<Badge variant="default" className="text-xs">
																	Primary
																</Badge>
															)}

															<Check
																className={cn(
																	"h-4 w-4",
																	isSelected ? "opacity-100" : "opacity-0",
																)}
															/>
														</div>
													</CommandItem>
												);
											});
										})()}
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>

					{showDescription && (
						<FormDescription>
							{globalProps.draftPicKpiEnabled
								? t("draft.admin_pic.supporting_description", {
										defaultValue:
											"Selain L1/L2/L3 di atas. Yang bisa membuka draf ini = ketiga slot + daftar ini.",
									})
								: t("draft.admin_pic.multi_description", {
										defaultValue:
											"Select one or more admins who will be responsible for this appointment.",
									})}
						</FormDescription>
					)}
					<FormMessage />
				</FormItem>
			)}
		/>
	);
});

export default AdminPicMultiSelector;
